import useSafeState from '../../utils/safe-state';
import React, {useEffect, useRef} from "react";
import {Form, Row, Col, Input, Button, message, Spin, Tooltip, Select, Table} from 'antd';
import {Trans} from "@lingui/macro";
import {useAxios} from "../../utils/hooks";
import { AxiosResponse, AxiosError } from 'axios';

import {antdContext} from "../../utils/antdContext";
import { InputNumber } from 'antd';
import { openNotification } from '../../utils/openNotification';
import {InfoCircleTwoTone, DeleteTwoTone} from "@ant-design/icons/lib";
import {CargoModel} from "../../models/cargo/cargo";



interface Interface{
    data?: CargoModel
    cargoId?: any;
    mode?: "View" | "AddNew" | "Edit";
}

const NewCargoForm = (props : Interface) => {

    const data: any = props.data || undefined;

    const { TextArea } = Input;

    const [form] = Form.useForm();
    const weightInputRef = useRef<any>(null);
    const maxRollSequenceRef = useRef<{[key: string]: number}>({});
    
    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const config = {headers: {'Content-Type': 'application/json'}};

    
    const [loading, setLoading] = useSafeState(false);
    const [isView, setIsView] = useSafeState<boolean>();
    const [isEdit, setIsEdit] = useSafeState<boolean>();
    
    const [cargoId, setCargoid] = useSafeState<string>();
    const [isSaveDisable, setIsSaveDisable] = useSafeState<boolean>(false);
    const [rollNumberEditable, setRollNumberEditable] = useSafeState<boolean>(false);

    const [items, setItems] = useSafeState<any>([]);
    const [fifoAndRollNumbers, setFifoAndRollNumbers] = useSafeState<any>([]);
    const [suppliers, setSuppliers] = useSafeState<any>([]);
    const [lineItems, setLineItems] = useSafeState<any>([]);

    const [rawItems, setRawItems] = useSafeState<any>([]);
    const [itemSearch, setItemSearch] = useSafeState<any>();
    const [fifoAndRollNumberSearch, setFifoAndRollNumberSearch] = useSafeState<any>();
    const [selectedItem, setSelectedItem] = useSafeState<any>();
    const [selectedSupplier, setSelectedSupplier] = useSafeState<any>();
    const originalRollNumberRef = useRef<string | undefined>(undefined);

    const [actProdQty, setActProdQty] = useSafeState<any>(0);


    const quantityOnChange = (value: number) => {
        form.setFieldsValue({'quantity':value});
    };

    const weightOnChange = (value: number) => {
        form.setFieldsValue({'weight':value});

        form.setFieldsValue({'producableQty':
                Math.round(
                    form.getFieldValue(["weight"]) && selectedItem && selectedItem['normWeight']
                ? form.getFieldValue(["weight"]) / selectedItem['normWeight']
                : 0
                )
        });

        setActProdQty(
            Math.round(
                form.getFieldValue(["weight"]) && selectedItem && selectedItem['normWeight']
                    ? form.getFieldValue(["weight"]) / selectedItem['normWeight']
                    : 0
            )
        );
    };


    const getItems = (itemNo?: any) => {
        if (axiosInstance.current != null) {
            axiosInstance.current
                .get('/resource/items')
                .then(function (response: AxiosResponse<any>) {
                    let filteredItems = response.data;
                    
                    // Filter by itemNo if provided
                    if (itemNo && itemNo !== '') {
                        filteredItems = response.data.filter((item: any) => 
                            item.itemNo.toLowerCase().includes(itemNo.toLowerCase())
                        );
                    }
                    
                    setRawItems(filteredItems);
                    const itemOptions = generateItemsOptions(filteredItems, 'manufacturer', 'itemNo');
                    setItems(itemOptions);
                })
                .catch(function (error: any) {
                    console.log(error);
                });
        }
    };
    const getRollNumberEditable = () => {
        if (axiosInstance.current != null) {
            axiosInstance.current
                .get('/resource/stock/roll-number-editable')
                .then(function (response: AxiosResponse<any>) {
                    setRollNumberEditable(!!response.data);
                })
                .catch(function (error: any) {
                    console.log(error);
                });
        }
    };

    const updateLineItemRollNumber = (key: any, value: string) => {
        setLineItems(lineItems.map((item: any) => item.key === key ? {...item, rollNumber: value} : item));
    };

    const getFifoAndRollNumbers = (itemNo?: any) => {
        if (axiosInstance.current != null) {
            axiosInstance.current
                .get('/resource/items')
                .then(function (response: AxiosResponse<any>) {
                    let filteredItems = response.data;
                    
                    // Filter by itemNo if provided
                    if (itemNo && itemNo !== '') {
                        filteredItems = response.data.filter((item: any) => 
                            item.itemNo === itemNo
                        );
                    }
                    
                    setRawItems(filteredItems);
                    const options = generateFifoOptions(filteredItems);
                    setFifoAndRollNumbers(options);
                })
                .catch(function (error: any) {
                    console.log(error);
                });
        }
    };

    const generateItemsOptions = (data: any, label: string, val: string): optionalType[] => {

        let arrayToOptions = new Array<optionalType>();
        for (var i = 0; i < data.length; i++) {
            // Item structure is flat: { fifoNumber, itemNo, quality, normWeight, ... }
            let currentItem: optionalType = { value: data[i]['itemNo'], label: data[i]['itemNo'] };
            if(!containsItem(arrayToOptions, data[i]['itemNo'])){
                arrayToOptions.push(currentItem);
            }
        }
        return arrayToOptions;
    };

    const containsItem = (list: optionalType[], itemNo: string): boolean => {

        for(var i = 0; i < list.length; i++){

            if(list[i].value === itemNo) {
                return true;
            }
        }

        return false;
    };

    const generateFifoOptions = (data: any): optionalType[] => {

        let arrayToOptions = new Array<optionalType>();
        for (var i = 0; i < data.length; i++) {
            // Item structure is flat: { fifoNumber, itemNo, rollNumber, ... }
            let currentItem: optionalType = { value: data[i]['fifoNumber'], label: data[i]['fifoNumber']};
            arrayToOptions.push(currentItem);
        }
        return arrayToOptions;
    };

    const generateOptions = (data: any, label: string): optionalType[] => {

        let arrayToOptions = new Array<optionalType>();
        for (var i = 0; i < data.length; i++) {
            let currentItem: optionalType = { value: data[i][label], label: data[i][label] };
            arrayToOptions.push(currentItem);
        }
        return arrayToOptions;
    };

    type optionalType = {
        key?: number;
        label: any;
        value: string;
    };

    useEffect(() => {
        getItems('');
        getRollNumberEditable();

        form.resetFields();

        if (props.mode === "Edit") {

            originalRollNumberRef.current = data.data.rollNumber;

            let fieldsValues = [];
            for (const [key, value] of Object.entries(data.data)) {
                fieldsValues.push({name: key, value: value});
                if(key == 'itemNo'){
                    getSuppliersByItemNo(value as string);
                }
            }
            form.setFields(fieldsValues);
        }else{
            form.setFieldsValue({'quantity':'1'});
        }

    }, []);


    useEffect(() => {
        if (itemSearch != '') {
            getItems(itemSearch);
        }
    }, [itemSearch]);

    useEffect(() => {

        if(selectedItem){
            console.log(selectedItem);

            form.setFieldsValue({
                normWeight: selectedItem.normWeight,
                sizeX: selectedItem.sizeX,
                sizeY: selectedItem.sizeY,
                quality: selectedItem.quality,
                producableQty:
                    Math.round(
                        form.getFieldValue('weight') / selectedItem.normWeight
                    )

            });
        }

    }, [selectedItem]);

    const handleFinish = () => {
        if (lineItems.length === 0) {
            message.error('Please add at least one line item before saving');
            return;
        }

            if(axiosInstance.current != null) {
            setLoading(true);

            const lineItemsPayload = lineItems.map((item: any) => ({
                itemNo: item.itemNo,
                fifoNumber: item.fifoNumber,
                rollNumber: item.rollNumber,
                weight: item.weight,
                supplier: item.supplier,
                remark: item.remark
            }));

            const cargoData = {
                deliveryNoteNumber: form.getFieldValue(["deliveryNoteNumber"]),
                remark: form.getFieldValue(["remark"]),
                lineItems: lineItemsPayload
            };

            axiosInstance
                .current
                .post(
                    "/resource/bejovoeses",
                    cargoData
                ).then(function (response : AxiosResponse<any>)
                {      
                    openNotification('success','');
                    setLoading(false);
                    setIsSaveDisable(true);
                    onReset();
                }).catch(function (error :AxiosError) {
                    let errResp = JSON.parse(JSON.stringify(error.response));
                    let errMessage = errResp.data.message;
                    openNotification('error',errMessage);
                    setLoading(false);
                });

        }
    }

    const handleUpdate = () => {
        if(axiosInstance.current != null) {
            setLoading(true);

            const cargoData = {
                rollNumber: form.getFieldValue(["rollNumber"]),
                item: {
                    fifoNumber: form.getFieldValue(["fifoNumber"]),
                },
                supplier: {
                    id: form.getFieldValue(["supplier"])
                },
                weight: form.getFieldValue(["weight"]),
                deliveryNoteNumber: form.getFieldValue(["deliveryNoteNumber"]),
                remark: form.getFieldValue(["remark"]),
                status: form.getFieldValue(["status"]),
                version: form.getFieldValue(["version"])
            };

            axiosInstance
                .current
                .put(
                    "/resource/stock/id?id=" + encodeURIComponent(originalRollNumberRef.current!),
                    cargoData,
                    config
                ).then(function (response : AxiosResponse<any>) {
                    openNotification('success','');
                    setLoading(false);
                    setIsSaveDisable(true);
                    onReset();
                }).catch(function (error :AxiosError) {
                    console.log(error);
                    // let errResp = JSON.parse(JSON.stringify(error.response));
                    // let errMessage = errResp.data.message;
                    // openNotification('error',errMessage);
                }).finally(() => {
                    setLoading(false);
                });

        }
    }


    const handleItemChange = (value: any) => {
        form.resetFields(['quality', 'normWeight', 'sizeX', 'sizeY', 'producableQty', 'fifoNumber', 'rollNumber', 'supplier']);
        setSelectedItem(null);
        setSuppliers([]);
        getFifoAndRollNumbers(value);
        if (value) {
            getSuppliersByItemNo(value);
        }
    };

    const handleSupplierChange = (value: any) => {
        setSelectedSupplier(value);
        if (value) {
            getItemsBySupplier(value);
        } else {
            getItems('');
        }
    };

    const getNextRollNumber = (fifo: string, currentLineItems: any[]) => {
        const existingWithSameFifo = currentLineItems.filter((item: any) => item.fifoNumber == fifo);
        const knownMax = maxRollSequenceRef.current[fifo] || 0;

        const applyNext = (calculated: number) => {
            const nextSeq = Math.max(calculated, knownMax);
            maxRollSequenceRef.current[fifo] = nextSeq;
            form.setFieldsValue({'rollNumber': fifo + '/' + nextSeq});
        };

        if (existingWithSameFifo.length === 0) {
            if (axiosInstance.current != null) {
                axiosInstance.current
                    .get('/resource/stock/get-next-roll-number', { params: { fifoNumber: fifo } })
                    .then(function (response: AxiosResponse<any>) {
                        const parts = String(response.data).split('/');
                        applyNext(parts.length === 2 ? parseInt(parts[1]) : 1);
                    })
                    .catch(function (error: any) {
                        console.log(error);
                        applyNext(Math.max(1, knownMax));
                    });
            } else {
                applyNext(Math.max(1, knownMax));
            }
        } else {
            const maxInList = existingWithSameFifo.reduce((max: number, item: any) => {
                const parts = item.rollNumber.split('/');
                return parts.length === 2 ? Math.max(max, parseInt(parts[1])) : max;
            }, 0);
            applyNext(maxInList + 1);
        }
    };

    const getSuppliersByItemNo = (itemNo: string) => {
        if (axiosInstance.current != null) {
            axiosInstance.current
                .get('/resource/items/suppliers-by-item', { params: { itemNo } })
                .then(function (response: AxiosResponse<any>) {
                    const supplierOptions = response.data.map((s: any) => ({
                        value: s.id,
                        label: s.name
                    }));
                    setSuppliers(supplierOptions);
                })
                .catch(function (error: any) {
                    console.log(error);
                    setSuppliers([]);
                });
        }
    };

    const getItemsBySupplier = (supplierId: any) => {
        if (axiosInstance.current != null) {
            axiosInstance.current
                .get('/resource/items/items-by-supplier', { params: { supplierId } })
                .then(function (response: AxiosResponse<any>) {
                    const itemOptions = response.data.map((itemNo: string) => ({
                        value: itemNo,
                        label: itemNo
                    }));
                    setItems(itemOptions);
                })
                .catch(function (error: any) {
                    console.log(error);
                });
        }
    };

    const getSuppliersByFifo = (fifoNumber: number) => {
        if (axiosInstance.current != null) {
            axiosInstance.current
                .get('/resource/items/suppliers', { params: { fifoNumber } })
                .then(function (response: AxiosResponse<any>) {
                    const supplierOptions = response.data.map((s: any) => ({
                        value: s.id,
                        label: s.name
                    }));
                    setSuppliers(supplierOptions);
                })
                .catch(function (error: any) {
                    console.log(error);
                    setSuppliers([]);
                });
        }
    };

    const handleFifoAndRollNumberChange = (value: any) => {
        form.resetFields(['quality', 'normWeight', 'sizeX', 'sizeY', 'producableQty', 'rollNumber', 'supplier']);
        for(let i = 0; i < rawItems.length; i++){
            if(rawItems[i]['fifoNumber'] == value) {
                setSelectedItem(rawItems[i]);
                const fifo = rawItems[i]['fifoNumber'];
                
                getNextRollNumber(fifo, lineItems);
                getSuppliersByFifo(fifo); // Fetch suppliers from backend
                
                return;
            }
        }
    };

    const addLineItem = () => {
        const item = form.getFieldValue('itemNo');
        const fifo = form.getFieldValue('fifoNumber');
        const rollNum = form.getFieldValue('rollNumber');
        const supplier = form.getFieldValue('supplier');
        const weight = form.getFieldValue('weight');

        if (!item || !fifo || !rollNum || !supplier || !weight) {
            message.error('Please fill all required fields before adding a line item');
            return;
        }
        const newLineItem = {
            key: Date.now(),
            fifoNumber: fifo,
            rollNumber: rollNum,
            itemNo: selectedItem ? selectedItem.itemNo : item,
            weight: weight,
            supplier: supplier
        };

        const updatedLineItems = [...lineItems, newLineItem];
        setLineItems(updatedLineItems);
        
        form.resetFields(['weight', 'rollNumber']);
        form.setFieldsValue({ weight: null });
        setActProdQty(0);
        
        if (fifo) {
            getNextRollNumber(fifo, updatedLineItems);
        }
        
        setTimeout(() => {
            if (weightInputRef.current) {
                weightInputRef.current.focus();
            }
        }, 100);
    };

    const deleteLineItem = (key: any) => {
        setLineItems(lineItems.filter((item: any) => item.key !== key));
    };

    const onReset = () => {
        setSelectedItem(null);
        setSelectedSupplier(null);
        setLineItems([]);
        maxRollSequenceRef.current = {};
        form.resetFields();
        form.resetFields(['quality', 'normWeight', 'sizeX', 'sizeY', 'producableQty']);
    };



    return(
        
                <Form id="newcargo"  layout='vertical' form={form}
                      onFinish={() => props.mode === "Edit" ? handleUpdate() : handleFinish()}>
                <Spin spinning={loading}></Spin>
                <>
                    <Row gutter={24}>
                        <Col span={24}>
                            <Form.Item
                                name="deliveryNoteNumber"
                                label={<Trans>Delivery Note Number</Trans>}
                                children={<Input disabled={props.mode === "Edit"}/>}
                                rules={[{
                                    type:"string",
                                    required: true,
                                    message: <Trans>Please enter the cargo name</Trans>
                                }]}
                            />
                        </Col>
                    </Row>

                    <Row gutter={24}>
                        <Col span={16}>
                            <Form.Item
                                name="itemNo"
                                label={
                                    <>
                                        <Trans>Item</Trans>
                                        <Tooltip
                                            placement="top"
                                            title={
                                                <span>
                                            A lista maximum 50 elemet jelenít meg, és a tartalma a beírt szöveg
                                            alapján módosul.
                                        </span>
                                            }
                                            children={
                                                <InfoCircleTwoTone twoToneColor="#1890ff" style={{ marginLeft: 5 }} />
                                            }
                                        />
                                    </>
                                }
                                children={
                                    <Select
                                        allowClear
                                        showSearch
                                        style={{ width: '100%' }}
                                        placeholder={<Trans>Select an Item</Trans>}
                                        onSearch={value => setItemSearch(value)}
                                        onChange={value => handleItemChange(value)}
                                        filterOption={false}
                                        options={items}
                                        disabled={props.mode === "Edit"}
                                    ></Select>
                                }
                                rules={[
                                    {
                                        required: false,
                                        message: <Trans>Please enter a shop</Trans>,
                                    },
                                ]}
                            />
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="fifoNumber"
                                label={
                                    <>
                                        <Trans>Fifo</Trans>
                                    </>
                                }
                                children={
                                    <Select
                                        allowClear
                                        showSearch
                                        style={{ width: '100%' }}
                                        placeholder={<Trans>Select a FIFO</Trans>}
                                        onSearch={value => setFifoAndRollNumberSearch(value)}
                                        onChange={value => handleFifoAndRollNumberChange(value)}
                                        filterOption={false}
                                        options={fifoAndRollNumbers}
                                        disabled={props.mode === "Edit"}
                                    ></Select>
                                }
                                rules={[
                                    {
                                        required: false,
                                        message: <Trans>Please enter a FIFO</Trans>,
                                    },
                                ]}
                            />
                        </Col>
                    </Row>

                    <Row gutter={24}>
                        <Col span={24}>
                            <Form.Item
                                hidden={props.mode !== "Edit"}
                                name="rollNumber"
                                label={<Trans>Roll Number</Trans>}
                                children={<Input disabled={props.mode === "Edit"}/>}
                                rules={[{
                                    type:"string",
                                    required: true,
                                    message: <Trans>Please enter the cargo name</Trans>
                                }]}
                            />
                        </Col>

                        <Col span={24}>
                            <Form.Item
                                name="supplier"
                                label={<Trans>Supplier</Trans>}
                                children={
                                    <Select
                                        allowClear
                                        showSearch
                                        style={{ width: '100%' }}
                                        placeholder={<Trans>Select a Supplier</Trans>}
                                        onChange={value => handleSupplierChange(value)}
                                        filterOption={true}
                                        options={suppliers}
                                    ></Select>
                                }
                                rules={[
                                    {
                                        required: false,
                                        message: <Trans>Please select a supplier</Trans>,
                                    },
                                ]}
                            />
                        </Col>
                    </Row>

                    <Row gutter={24} style={{backgroundColor: '#fafafa', padding: '12px', borderRadius: '4px', marginBottom: '16px'}}>
                        <Col span={6}>
                            <div style={{textAlign: 'center'}}>
                                <div style={{fontWeight: 'bold', marginBottom: '4px'}}><Trans>Quality</Trans></div>
                                <div style={{fontSize: '16px'}}>{form.getFieldValue('quality') || '-'}</div>
                            </div>
                            <Form.Item name="quality" hidden><Input /></Form.Item>
                        </Col>
                        <Col span={6}>
                            <div style={{textAlign: 'center'}}>
                                <div style={{fontWeight: 'bold', marginBottom: '4px'}}><Trans>Norm Weight</Trans></div>
                                <div style={{fontSize: '16px'}}>{form.getFieldValue('normWeight') || '-'}</div>
                            </div>
                            <Form.Item name="normWeight" hidden><Input /></Form.Item>
                        </Col>
                        <Col span={6}>
                            <div style={{textAlign: 'center'}}>
                                <div style={{fontWeight: 'bold', marginBottom: '4px'}}><Trans>Size X</Trans></div>
                                <div style={{fontSize: '16px'}}>{form.getFieldValue('sizeX') || '-'}</div>
                            </div>
                            <Form.Item name="sizeX" hidden><Input /></Form.Item>
                        </Col>
                        <Col span={6}>
                            <div style={{textAlign: 'center'}}>
                                <div style={{fontWeight: 'bold', marginBottom: '4px'}}><Trans>Size Y</Trans></div>
                                <div style={{fontSize: '16px'}}>{form.getFieldValue('sizeY') || '-'}</div>
                            </div>
                            <Form.Item name="sizeY" hidden><Input /></Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={24} align="middle">
                        <Col span={6}>
                            <Form.Item
                                name="weight"
                                label={<Trans>Súly (kg)</Trans>}
                                children={<InputNumber 
                                    ref={weightInputRef}
                                    disabled={isView}
                                    min={1}
                                    onChange={weightOnChange} 
                                    style={{width: '100%'}}
                                    onPressEnter={addLineItem}
                                />}
                                rules={[{
                                    type:"number",
                                    required: true,
                                    message: <Trans>Please enter the weight</Trans>,
                                    validator: () => {

                                        let v: any = form.getFieldValue('weight');

                                        if (v == undefined || v.length <= 0) {
                                            return Promise.reject('');
                                        } else {
                                            return Promise.resolve('');
                                        }
                                    }
                                }]}
                            />
                        </Col>
                        <Col span={6}>
                            <div style={{textAlign: 'center', backgroundColor: '#fafafa', padding: '8px', borderRadius: '4px'}}>
                                <div style={{fontWeight: 'bold', fontSize: '12px', marginBottom: '4px'}}><Trans>Gyártható mennyiség</Trans></div>
                                <div style={{fontSize: '18px', fontWeight: 'bold'}}>{actProdQty || 0}</div>
                            </div>
                            <Form.Item name="producableQty" hidden><Input /></Form.Item>
                        </Col>
                        <Col span={12} style={{display: 'flex', justifyContent: 'flex-end', paddingTop: '4px'}}>
                            <Button hidden={props.mode === "Edit"} type="primary" onClick={addLineItem} size="large">
                                <Trans>Hozzáadás a listához</Trans>
                            </Button>
                        </Col>
                    </Row>

                    <Row gutter={24} style={{marginTop: '16px'}}>
                        <Col span={24} hidden={props.mode === "Edit"}>
                            <Table
                                dataSource={lineItems}
                                pagination={false}
                                size="small"
                                bordered
                                columns={[
                                    {
                                        title: <Trans>Roll Number</Trans>,
                                        dataIndex: 'rollNumber',
                                        key: 'rollNumber',
                                        width: '25%',
                                        render: (text: string, record: any) => rollNumberEditable
                                            ? <Input
                                                value={text}
                                                onChange={(e) => updateLineItemRollNumber(record.key, e.target.value)}
                                              />
                                            : text,
                                    },
                                    {
                                        title: <Trans>FIFO</Trans>,
                                        dataIndex: 'fifoNumber',
                                        key: 'fifoNumber',
                                        width: '20%',
                                    },
                                    {
                                        title: <Trans>Item No</Trans>,
                                        dataIndex: 'itemNo',
                                        key: 'itemNo',
                                        width: '25%',
                                    },
                                    {
                                        title: <Trans>Weight</Trans>,
                                        dataIndex: 'weight',
                                        key: 'weight',
                                        width: '15%',
                                    },
                                    {
                                        title: '',
                                        key: 'action',
                                        width: '15%',
                                        align: 'center' as const,
                                        render: (text: any, record: any) => (
                                            <DeleteTwoTone 
                                                onClick={() => deleteLineItem(record.key)}
                                                twoToneColor="#ff4d4f"
                                                style={{ cursor: 'pointer', fontSize: '16px' }}
                                            />
                                        ),
                                    },
                                ]}
                                locale={{emptyText: <Trans>Még nincs hozzáadott tétel</Trans>}}
                            />
                        </Col>
                    </Row>

                    <Row gutter={24} style={{marginTop: '16px'}}>
                        <Col span={24}>
                            <Form.Item
                                name="remark"
                                label={<Trans>Comment</Trans>}
                                children={<TextArea disabled={isView} rows={4}/>}
                            />
                        </Col>
                    </Row>
                </>
                {props.mode === "Edit" ?
                    <Button loading={loading} htmlType="button" type="primary" onClick={() => form.submit()}>
                        <Trans>Mentés</Trans>
                    </Button>
                    :
                        <>
                            <Button htmlType="button" style={{marginRight: 8}} onClick={() => onReset()}>
                                <Trans>Vissza</Trans>
                            </Button>
                            <Button loading={loading} htmlType="button" type="primary" style={{marginRight: 8}}
                                    onClick={() => form.submit()}>
                                <Trans>Save</Trans>
                            </Button>
                        </>
                }

            </Form>
            );
            

}
export default NewCargoForm;
